(() => {
  if (customElements.get('nm-bundle-builder')) return;

  class NMBundleBuilder extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.strings = JSON.parse(this.querySelector('[data-nm-bundle-strings]')?.textContent || '{}');
      this.tabs = [...this.querySelectorAll('[data-nm-bundle-tab]')];
      this.mount = this.querySelector('[data-nm-bundle-mount]');
      this.panel = this.querySelector('[data-nm-bundle-panel]');
      const requestedOffer = new URL(window.location.href).searchParams.get('bundle');
      this.activeOffer = this.tabs.some(tab => tab.dataset.nmBundleTab === requestedOffer) ? requestedOffer : this.tabs[0]?.dataset.nmBundleTab || 'shirt';
      this.busy = false;
      this.boundClick = this.onClick.bind(this);
      this.boundKeydown = this.onKeydown.bind(this);
      this.boundSubmit = this.onSubmit.bind(this);
      this.addEventListener('click', this.boundClick);
      this.addEventListener('keydown', this.boundKeydown);
      this.addEventListener('submit', this.boundSubmit);
      this.applyOffer(this.tabs.find(tab => tab.dataset.nmBundleTab === this.activeOffer));
      this.activateWidget();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      clearTimeout(this.loadingTimer);
      this.removeEventListener('click', this.boundClick);
      this.removeEventListener('keydown', this.boundKeydown);
      this.removeEventListener('submit', this.boundSubmit);
      this.widgetVersion = (this.widgetVersion || 0) + 1;
      this.initialized = false;
    }

    onClick(event) {
      const tab = event.target.closest('[data-nm-bundle-tab]');
      if (!tab || !this.contains(tab)) return;
      this.selectOffer(tab);
    }

    onKeydown(event) {
      const tab = event.target.closest('[data-nm-bundle-tab]');
      if (!tab || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      const current = this.tabs.indexOf(tab);
      let next = current;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = this.tabs.length - 1;
      if (event.key === 'ArrowRight') next = (current + 1) % this.tabs.length;
      if (event.key === 'ArrowLeft') next = (current + this.tabs.length - 1) % this.tabs.length;
      if (this.busy) return;
      this.selectOffer(this.tabs[next]);
      this.tabs[next]?.focus();
    }

    selectOffer(tab) {
      const offer = tab.dataset.nmBundleTab;
      if (this.busy || offer === this.activeOffer) return;
      const url = new URL(window.location.href);
      url.searchParams.set('bundle', offer);
      window.location.assign(url.href);
    }

    applyOffer(tab) {
      if (!tab) return;
      const offer = tab.dataset.nmBundleTab;
      const nextOffer = this.mount.querySelector(`[data-nm-bundle-offer="${offer}"]`);
      if (!nextOffer) return;
      this.mount.querySelectorAll('[data-nm-bundle-offer]').forEach(item => { item.hidden = item !== nextOffer; });
      this.activeOffer = offer;
      this.tabs.forEach(item => {
        const active = item === tab;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-selected', String(active));
        item.tabIndex = active ? 0 : -1;
      });
      this.panel.setAttribute('aria-labelledby', tab.id);
      this.querySelector('.nm-bundle__offer-title').textContent = tab.dataset.nmBundleTitle;
      const productLink = this.querySelector('[data-nm-bundle-product-link]');
      productLink.href = offer === 'shirt' ? productLink.dataset.shirtUrl : productLink.dataset.breechUrl;
    }

    async activateWidget() {
      const version = this.widgetVersion = (this.widgetVersion || 0) + 1;
      this.mount.querySelectorAll('[data-nm-bundle-widget]').forEach(host => host.replaceChildren());
      const form = this.mount.querySelector(`[data-nm-bundle-offer="${this.activeOffer}"] form`);
      const host = form?.querySelector('[data-nm-bundle-widget]');
      if (!host) return;
      const loading = form.querySelector('[data-nm-bundle-loading]');
      loading.hidden = false;
      loading.textContent = this.strings.loading;
      form.querySelector('[data-nm-bundle-add]').disabled = true;
      form.querySelector('[data-nm-bundle-error]').hidden = true;
      try {
        for (let attempt = 0; attempt < 50 && !window.ARMATEQCart?.loadBundleSdk; attempt++) await new Promise(resolve => setTimeout(resolve, 100));
        if (!window.ARMATEQCart?.loadBundleSdk) throw new Error(this.strings.unavailable);
        const config = JSON.parse(document.getElementById('kaching-bundles-config')?.textContent || '{}');
        if (!config.shopifyDomain || !config.storefrontAccessToken) throw new Error(this.strings.unavailable);
        await window.ARMATEQCart.loadBundleSdk();
        if (version !== this.widgetVersion || !this.isConnected) return;
        const widget = document.createElement('kaching-bundle-component');
        widget.setAttribute('shop-domain', config.shopifyDomain);
        widget.setAttribute('storefront-token', config.storefrontAccessToken);
        widget.setAttribute('product-id', host.dataset.productId);
        widget.setAttribute('selected-variant-id', host.dataset.variantId);
        widget.setAttribute('country', this.dataset.country);
        widget.setAttribute('language', this.dataset.language.split('-')[0]);
        host.append(widget);
        this.watchWidget();
      } catch (error) {
        if (version === this.widgetVersion) loading.textContent = this.strings.unavailable;
      }
    }

    selectionState(form) {
      const widget = form?.querySelector('kaching-bundle, kaching-bundle-component');
      const selected = widget?.querySelector('.kaching-bundles__bar--selected');
      if (!selected) return { ready: false, valid: false, message: this.strings.loading };
      const unfinished = [...selected.querySelectorAll('.kaching-bundles__collection-product__choose-product-button, .kaching-bundles__collection-product__choose-product-image')].find(element => element.getClientRects().length > 0);
      if (unfinished) return { ready: true, valid: false, message: this.strings.choose_items, target: unfinished };
      return { ready: true, valid: true };
    }

    watchWidget() {
      const form = this.mount.querySelector(`[data-nm-bundle-offer="${this.activeOffer}"] form`);
      const widget = form?.querySelector('kaching-bundle-component');
      if (!form || !widget) return;
      const update = () => {
        const state = this.selectionState(form);
        form.querySelector('[data-nm-bundle-add]').disabled = this.busy || !state.ready;
        const loading = form.querySelector('[data-nm-bundle-loading]');
        if (state.ready) {
          loading.hidden = true;
          clearTimeout(this.loadingTimer);
        }
      };
      this.observer = new MutationObserver(update);
      this.observer.observe(widget, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'aria-selected', 'aria-checked'] });
      this.loadingTimer = setTimeout(() => {
        if (this.selectionState(form).ready) return;
        form.querySelector('[data-nm-bundle-loading]').textContent = this.strings.unavailable;
      }, 15000);
      update();
    }

    showError(form, message, target) {
      const error = form.querySelector('[data-nm-bundle-error]');
      error.textContent = message;
      error.hidden = false;
      if (target?.focus) target.focus();
      else {
        error.tabIndex = -1;
        error.focus();
      }
    }

    async onSubmit(event) {
      const form = event.target.closest('form[data-nm-kaching-form]');
      if (!form || !this.mount.contains(form) || form.closest('[data-nm-bundle-offer]')?.hidden) return;
      event.preventDefault();
      if (this.busy) return;
      const state = this.selectionState(form);
      if (!state.valid) {
        this.showError(form, state.message, state.target);
        return;
      }
      this.busy = true;
      const button = form.querySelector('[data-nm-bundle-add]');
      const label = button.querySelector('[data-nm-bundle-button-text]');
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      label.textContent = this.strings.adding;
      form.querySelector('[data-nm-bundle-error]').hidden = true;
      this.tabs.forEach(tab => { tab.disabled = true; });
      try {
        await Promise.resolve();
        const data = form.querySelector('kaching-bundle-component')?.items();
        if (!Array.isArray(data) || !data.length) throw new Error(this.strings.choose_items);
        if (window.ARMATEQCart?.addItems) {
          await window.ARMATEQCart.addItems(data);
        } else {
          const root = window.Shopify?.routes?.root || '/';
          const response = await fetch(`${root}cart/add.js`, {
            method: 'POST',
            headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify({ items: data })
          });
          const result = await response.json();
          if (!response.ok || result.status) throw new Error(result.description || result.message || this.strings.error);
          window.location.assign(`${root}cart`);
        }
      } catch (error) {
        this.showError(form, error.message || this.strings.error);
      } finally {
        this.busy = false;
        button.disabled = !this.selectionState(form).ready;
        button.removeAttribute('aria-busy');
        label.textContent = this.strings.add_selection;
        this.tabs.forEach(tab => { tab.disabled = false; });
      }
    }
  }

  customElements.define('nm-bundle-builder', NMBundleBuilder);
})();
